import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { InterpellationDialog } from '../components/InterpellationDialog';
import {
  Sprout,
  Users,
  MapPin,
  Scale,
  TrendingUp,
  CheckCircle2,
  Megaphone,
  HeartHandshake,
  Link2,
  Facebook,
  Mail,
  Share2,
  Vote,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 10) return "à l'instant";
  if (diff < 60) return `il y a ${diff}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `il y a ${days} j`;
  return new Date(iso).toLocaleDateString('fr-FR');
}

export default function Petition() {
  const petitionUrl = typeof window !== 'undefined' ? window.location.href.split('#')[0] : '';
  const shareText = "Je signe la pétition « L'habitat léger doit compter » — Rejoignez la campagne citoyenne pour la présidentielle 2027.";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(petitionUrl);
      toast.success('Lien copié dans le presse-papiers');
    } catch {
      toast.error('Impossible de copier le lien');
    }
  };

  const shareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(petitionUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent("Je signe la pétition pour l'habitat léger");
    const body = encodeURIComponent(`${shareText}\n\n${petitionUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const [form, setForm] = useState({
    name: '',
    email: '',
    postal_code: '',
    city: '',
    comment: '',
    newsletter: true,
    public_display: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [count, setCount] = useState(null);
  const [recent, setRecent] = useState([]);
  const [interpelOpen, setInterpelOpen] = useState(false);

  // Real-time count + recent signatures polling
  useEffect(() => {
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const [c, r] = await Promise.all([
          axios.get(`${API}/petition/count`),
          axios.get(`${API}/petition/recent?limit=15`)
        ]);
        if (cancelled) return;
        setCount(c.data.count);
        setRecent(r.data);
      } catch {
        /* silent */
      }
    };
    fetchLive();
    const interval = setInterval(fetchLive, 8000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Nom et email sont requis');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/petition/sign`, form);
      setSubmitted(true);
      toast.success('Merci pour votre signature !');
      setCount((c) => (c || 0) + 1);
      if (form.public_display) {
        setRecent((prev) => [
          {
            name: form.name,
            city: form.city,
            postal_code: form.postal_code,
            comment: form.comment,
            created_at: new Date().toISOString(),
            _justSigned: true,
          },
          ...prev,
        ].slice(0, 15));
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('Cet email a déjà signé la pétition');
      } else {
        toast.error("Erreur lors de l'envoi");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-emerald-500/5"
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-14 items-start">
            {/* Left: title & pitch */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6">
                <Megaphone className="w-3.5 h-3.5" />
                Campagne présidentielle 2027
              </div>
              <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
                L'habitat léger <span className="text-primary">doit compter</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                Une campagne citoyenne pour faire évoluer le droit d'habiter
                autrement, sans renoncer à vivre dignement, à travailler la terre
                et à protéger le vivant.
              </p>

              {/* Real-time counter */}
              <div
                className="mt-10 flex items-center gap-4 flex-wrap"
                data-testid="petition-count-badge"
              >
                <div className="flex items-center gap-3 px-6 py-4 rounded-2xl bg-background border border-border shadow-sm">
                  <div className="relative">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="font-serif text-3xl sm:text-4xl font-bold text-foreground leading-none tabular-nums">
                      {count !== null ? count.toLocaleString('fr-FR') : '—'}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wide">
                      signataire{count === 1 ? '' : 's'} · temps réel
                    </div>
                  </div>
                </div>
                <a
                  href="#signer"
                  className="text-sm font-medium text-primary hover:underline"
                  data-testid="petition-cta-sign"
                >
                  Je signe maintenant →
                </a>
              </div>
            </div>

            {/* Right: live recent signatures */}
            <aside
              className="lg:sticky lg:top-24 bg-background/80 backdrop-blur rounded-2xl border border-border shadow-sm overflow-hidden"
              data-testid="petition-recent-sidebar"
            >
              <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-border bg-muted/40">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
                  Dernières signatures
                </div>
                <span className="text-xs text-muted-foreground">
                  en direct
                </span>
              </div>
              <ul className="max-h-[420px] overflow-y-auto divide-y divide-border">
                {recent.length === 0 ? (
                  <li className="px-5 py-8 text-center text-sm text-muted-foreground">
                    Soyez le premier à signer !
                  </li>
                ) : (
                  recent.map((s, idx) => (
                    <li
                      key={`${s.created_at}-${idx}`}
                      className={`px-5 py-3 transition-colors ${s._justSigned ? 'bg-emerald-50 dark:bg-emerald-950/20' : ''}`}
                      data-testid={`petition-recent-item-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-serif font-bold text-sm flex-shrink-0">
                          {(s.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">
                            {s.name}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {[s.postal_code, s.city].filter(Boolean).join(' ') || 'France'} · {timeAgo(s.created_at)}
                          </div>
                          {s.comment && (
                            <div className="text-xs text-muted-foreground italic mt-1 line-clamp-2">
                              « {s.comment} »
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </aside>
          </div>
        </div>
      </section>

      {/* Context */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-neutral max-w-none">
          <p className="text-lg leading-relaxed text-foreground">
            Et si la présidentielle de 2027 devenait l'occasion de faire
            avancer un droit essentiel : celui de pouvoir habiter autrement,
            sans renoncer à vivre dignement, à travailler la terre et à
            protéger le vivant ?
          </p>
          <p className="text-base leading-relaxed text-muted-foreground mt-6">
            Aujourd'hui, des personnes souhaitent choisir un habitat plus sobre
            et réversible. Des paysans peinent à se loger à proximité de leur
            activité. Des communes voudraient accueillir des projets
            écologiques, mais manquent de cadre juridique adapté.
          </p>
          <p className="text-base leading-relaxed text-foreground mt-6 font-medium">
            Nous voulons faire évoluer cette situation.
          </p>
        </div>

        {/* Two proposals */}
        <div className="mt-16">
          <h2 className="font-serif font-bold text-3xl mb-8">
            Deux propositions complémentaires
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-8 border-2 hover:border-primary/40 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                <Sprout className="w-6 h-6" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3">
                Reconnaître l'habitat paysan réversible
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Permettre aux personnes qui développent une activité agricole
                de vivre à proximité de leur ferme, dans un cadre adapté et
                protecteur des terres.
              </p>
            </Card>
            <Card className="p-8 border-2 hover:border-emerald-500/40 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-5">
                <MapPin className="w-6 h-6" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3">
                Créer des Zones d'écologie communale
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Un nouvel outil d'urbanisme permettant aux collectivités
                d'accueillir des projets associant habitat réversible,
                agriculture écologique et préservation des milieux naturels.
              </p>
            </Card>
          </div>
          <p className="text-sm text-muted-foreground mt-6 italic">
            Ces propositions s'appuient sur plusieurs années de travail de
            terrain, d'expérience juridique et de mobilisation. Elles sont
            portées par le <strong>Collectif de défense de l'habitat léger</strong>{' '}
            et <strong>Désobéissance Fertile</strong>, qui travaillent à
            transformer des situations concrètes en propositions politiques et
            législatives.
          </p>
        </div>

        {/* Why join */}
        <div className="mt-20">
          <h2 className="font-serif font-bold text-3xl mb-4">
            Pourquoi rejoindre la campagne ?
          </h2>
          <p className="text-muted-foreground mb-8 leading-relaxed">
            Parce qu'une signature peut peser davantage lorsqu'elle s'inscrit
            dans une mobilisation organisée. En rejoignant la campagne, vous
            pourrez :
          </p>
          <ul className="space-y-4">
            {[
              'Contribuer à faire entrer ces propositions dans le débat présidentiel',
              'Interpeller les candidates et candidats et suivre publiquement leurs réponses',
              'Rejoindre un réseau de personnes et de collectifs qui agissent sur ces sujets',
              'Participer à des actions locales et nationales, selon vos possibilités',
              'Contribuer à maintenir la pression après l\'élection, jusqu\'à ce que les engagements deviennent des avancées concrètes',
            ].map((item, idx) => (
              <li key={idx} className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-foreground leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Momentum */}
        <div className="mt-20 p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-emerald-500/5 border border-border">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-background flex items-center justify-center flex-shrink-0 shadow-sm">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-serif font-bold text-2xl mb-3">
                Un héritage de mobilisation
              </h3>
              <p className="text-base text-foreground leading-relaxed">
                Nous avons déjà rassemblé <strong>80 000 signatures</strong>{' '}
                autour de l'habitat léger et{' '}
                <strong>25 000 autour de l'habitat paysan</strong>. Nous
                voulons maintenant <strong>changer d'échelle</strong>.
              </p>
              <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                Cette campagne ne s'arrêtera pas au soir de l'élection. Nous
                voulons obtenir des engagements publics, les rendre vérifiables
                et suivre leur mise en œuvre avec les citoyens, les
                associations, les paysans et les collectivités.
              </p>
            </div>
          </div>
        </div>

        {/* Final call */}
        <div className="mt-16 text-center max-w-2xl mx-auto">
          <HeartHandshake className="w-12 h-12 text-primary mx-auto mb-4" />
          <p className="text-lg text-foreground leading-relaxed">
            Que vous soyez directement concerné par l'habitat réversible ou
            simplement convaincu qu'il faut ouvrir de nouvelles voies pour se
            loger, cultiver et préserver les territoires,{' '}
            <strong>votre place est dans cette mobilisation</strong>.
          </p>
          <p className="font-serif font-bold text-2xl mt-6 text-primary">
            Signez, rejoignez le mouvement et prenez part à la campagne.
          </p>
        </div>
      </section>

      {/* Signature form */}
      <section className="bg-muted/30 border-t border-border" id="signer">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {submitted ? (
            <Card className="p-10 text-center" data-testid="petition-success">
              <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
              <h3 className="font-serif font-bold text-2xl mb-3">
                Merci pour votre signature !
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Votre voix compte. Nous vous tiendrons informé·e des prochaines
                étapes de la campagne.
              </p>
              <p className="text-sm text-muted-foreground mt-6">
                Partagez cette campagne autour de vous — chaque signature
                renforce notre poids collectif.
              </p>
            </Card>
          ) : (            <Card className="p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-6">
                <Scale className="w-6 h-6 text-primary" />
                <h2 className="font-serif font-bold text-2xl">
                  Je signe la pétition
                </h2>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name">
                    Nom complet <span className="text-primary">*</span>
                  </Label>
                  <Input
                    id="name"
                    data-testid="petition-input-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="email">
                    Email <span className="text-primary">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="petition-input-email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    required
                    className="mt-1.5"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Code postal</Label>
                    <Input
                      id="postal_code"
                      data-testid="petition-input-postal"
                      value={form.postal_code}
                      onChange={(e) =>
                        setForm({ ...form, postal_code: e.target.value })
                      }
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Ville</Label>
                    <Input
                      id="city"
                      data-testid="petition-input-city"
                      value={form.city}
                      onChange={(e) =>
                        setForm({ ...form, city: e.target.value })
                      }
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="comment">
                    Pourquoi je signe (optionnel)
                  </Label>
                  <Textarea
                    id="comment"
                    data-testid="petition-input-comment"
                    rows={3}
                    value={form.comment}
                    onChange={(e) =>
                      setForm({ ...form, comment: e.target.value })
                    }
                    className="mt-1.5"
                    placeholder="Dites en quelques mots pourquoi cette cause vous tient à cœur…"
                  />
                </div>
                <div className="space-y-3 pt-2">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="public_display"
                      checked={form.public_display}
                      onCheckedChange={(v) =>
                        setForm({ ...form, public_display: !!v })
                      }
                      data-testid="petition-public-checkbox"
                    />
                    <label
                      htmlFor="public_display"
                      className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                    >
                      J'accepte que mon nom (sans mon email) soit visible
                      publiquement parmi les signataires.
                    </label>
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="newsletter"
                      checked={form.newsletter}
                      onCheckedChange={(v) =>
                        setForm({ ...form, newsletter: !!v })
                      }
                      data-testid="petition-newsletter-checkbox"
                    />
                    <label
                      htmlFor="newsletter"
                      className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                    >
                      Je souhaite recevoir les actualités de la campagne et
                      être tenu·e informé·e des actions à venir.
                    </label>
                  </div>
                </div>
                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="w-full"
                  data-testid="petition-submit-button"
                >
                  {submitting ? 'Envoi…' : 'Je signe la pétition'}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Vos données ne seront jamais revendues ni cédées à des tiers.
                </p>
              </form>
            </Card>
          )}

          {/* Interpellation */}
          <Card className="mt-10 p-8 text-center border-2 border-primary/20" data-testid="petition-interpellation-section">
            <Vote className="w-10 h-10 text-primary mx-auto mb-3" />
            <h3 className="font-serif font-bold text-2xl mb-2">Interpellez les candidats à la présidentielle</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Envoyez en quelques clics un message aux candidates et candidats pour leur demander de s'engager sur l'habitat léger.
            </p>
            <Button size="lg" onClick={() => setInterpelOpen(true)} className="gap-2" data-testid="petition-interpellate-btn">
              <Mail className="w-4 h-4" />
              Interpeller les candidats
            </Button>
          </Card>
          <InterpellationDialog open={interpelOpen} onOpenChange={setInterpelOpen} defaultName={form.name} />

          {/* Share buttons */}
          <div className="mt-10 pt-10 border-t border-border" data-testid="petition-share-section">
            <div className="flex items-center gap-2 justify-center mb-4 text-sm text-muted-foreground">
              <Share2 className="w-4 h-4" />
              Partagez la campagne autour de vous
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button
                variant="outline"
                size="lg"
                onClick={copyLink}
                className="gap-2"
                data-testid="petition-share-copy"
              >
                <Link2 className="w-4 h-4" />
                Copier le lien
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={shareFacebook}
                className="gap-2 hover:bg-[#1877F2]/10 hover:border-[#1877F2]/40 hover:text-[#1877F2]"
                data-testid="petition-share-facebook"
              >
                <Facebook className="w-4 h-4" />
                Facebook
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={shareEmail}
                className="gap-2"
                data-testid="petition-share-email"
              >
                <Mail className="w-4 h-4" />
                Email
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
